import type { CheckButtonProps } from '@hover/blueprint';
import { Box, CheckButton, Icon, Tooltip, transition } from '@hover/blueprint';
import { iCheck } from '@hover/icons';
import React, { useRef, useState } from 'react';

export interface CardCheckboxProps extends CheckButtonProps {
  isToolTipDisabled?: boolean;
  shouldShowCheckmark?: boolean;
  toolTipText?: string | null;
}

export const CardCheckbox: React.FC<CardCheckboxProps> = ({
  children,
  isChecked,
  isToolTipDisabled,
  onChange,
  shouldShowCheckmark = true,
  toolTipText,
  ...props
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  // useRef is used to reference the div around CheckButton, allowing Tooltip
  // to be positioned alongside--as opposed to wrapping--Checkbutton. This avoids
  // styling conflicts from the `sx` prop on CheckButton, affecting Tooltip's positioning.
  const targetRef = useRef(null);
  const handleMouseEnter = () => setShowTooltip(true);
  const handleMouseLeave = () => setShowTooltip(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowTooltip(false);
    onChange?.(e);
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={targetRef}
      style={{ flex: 1 }}
    >
      <CheckButton
        borderRadius={350}
        display="block"
        height="100%"
        isChecked={isChecked}
        minHeight={{ base: '85px', tablet: '175px', desktop: '175px' }}
        onChange={handleChange}
        padding={400}
        paddingY={400}
        sx={{
          '.button-label': {
            width: '100%',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            justifyContent: 'space-between',
          },
          span: {
            height: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          },
        }}
        {...props}
      >
        <Box
          flex={1}
          height="100%"
          justifyContent="space-between"
          width="100%"
          zIndex={1000}
        >
          {children}
          {shouldShowCheckmark && (
            <Icon
              alignSelf={{ base: 'center', tablet: 'flex-end' }}
              color="neutral.900"
              flexShrink={0}
              icon={iCheck}
              opacity={isChecked ? 1 : 0}
              size="small"
              {...transition.easing500('opacity')}
            />
          )}
        </Box>
      </CheckButton>
      {toolTipText && (
        <Tooltip
          isDisabled={isToolTipDisabled}
          isOpen={showTooltip}
          label={toolTipText}
          maxW="250px"
          padding={500}
          placement="bottom"
          width="fit-content"
        >
          <Box />
        </Tooltip>
      )}
    </div>
  );
};
